/**
 * The cold world: sun and moon directional lights (colour from atmospheric transmittance) and the sky LightProbe
 * (owned by SkyRenderer). Pure numbers live in lightLevels() so they are testable without WebGL.
 */
import * as THREE from 'three';
import type { WorldState } from '../world/state/types';
import { sunTransmittance } from './sky/atmosphere';
import type { RGB } from './skyPalette';

const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));

export interface LightLevels {
  sunColor: RGB;
  sunIntensity: number;
  moonIntensity: number;
  /** Sky radiance scale for the sun / moon sky-view LUTs. */
  skySun: number;
  skyMoon: number;
  townGlow: number;
  /** Tone-mapping exposure: the eye adapts at night so moonlight and lamps stay readable. */
  exposure: number;
}

export const SKY_SUN_ILLUMINANCE = 15;

/** Eye adaptation: log2 exposure keyed by sun altitude (twilight skies are ~100× darker than day). */
const EXPOSURE_KEYS: Array<[number, number]> = [
  [3, 0],
  [0, 1],
  [-4, 2.6],
  [-8, 3.8],
  [-12, 4.0],
  [-18, 4.1],
];

export function exposureForSunAlt(sunAltDeg: number): number {
  if (!(sunAltDeg < EXPOSURE_KEYS[0]![0])) return 1;
  const last = EXPOSURE_KEYS[EXPOSURE_KEYS.length - 1]!;
  if (sunAltDeg <= last[0]) return 2 ** last[1];
  let i = 0;
  while (sunAltDeg < EXPOSURE_KEYS[i + 1]![0]) i++;
  const [a0, e0] = EXPOSURE_KEYS[i]!;
  const [a1, e1] = EXPOSURE_KEYS[i + 1]!;
  return 2 ** (e0 + ((e1 - e0) * (sunAltDeg - a0)) / (a1 - a0));
}

/** Man-made lights keep a steady apparent brightness while the eye adapts: divide by exposure^0.85. */
export function emissiveCompensation(exposure: number): number {
  return 1 / Math.pow(Math.max(1, exposure), 0.85);
}

export function lightLevels(state: WorldState): LightLevels {
  const { sun, moon } = state.sky;
  const moonUp = clamp01((moon.altDeg + 1) / 6);
  const night = clamp01((-sun.altDeg - 2) / 10);
  const f = clamp01(moon.illuminatedFraction);
  const t = sunTransmittance(sun.altDeg);
  return {
    sunColor: t,
    sunIntensity: sun.altDeg > -1 ? 3.2 : 0,
    moonIntensity: 0.055 * f * moonUp * night,
    skySun: SKY_SUN_ILLUMINANCE,
    skyMoon: SKY_SUN_ILLUMINANCE * 0.0015 * f * moonUp,
    townGlow: 0.02 * night,
    exposure: exposureForSunAlt(sun.altDeg),
  };
}

export class Lighting {
  readonly sun = new THREE.DirectionalLight(0xffffff, 0);
  readonly moon = new THREE.DirectionalLight(0x9fb4ff, 0);

  addTo(scene: THREE.Scene): void {
    scene.add(this.sun, this.sun.target, this.moon, this.moon.target);
  }

  update(levels: LightLevels, sunDir: THREE.Vector3, moonDir: THREE.Vector3): void {
    this.sun.position.copy(sunDir).multiplyScalar(100);
    this.sun.color.setRGB(...levels.sunColor);
    this.sun.intensity = levels.sunIntensity;
    this.moon.position.copy(moonDir).multiplyScalar(100);
    this.moon.intensity = levels.moonIntensity;
  }
}
